// Form.tsx - полный файл с изменениями
import { SetStateAction } from 'react'
import { Button } from '../../../../../common/components/button/Button'
import { FileUpload } from '../../../../../common/components/fileUpload/FileUpload'
import { Input } from '../../../../../common/components/input/Input'
import { Textarea } from '../../../../../common/components/textarea/Textarea'
import { PromoInterface } from '../../../../../common/types/interfaces'
import classes from './Form.module.sass'
import { useForm } from './hooks/useForm'
import { Toggle } from '../../../../../common/components/toggle/Toggle'
import { format } from 'date-fns'
import { SearchGroupSelect } from '../../../../../common/components/searchGroupSelect/SearchGroupSelect'

type Props = {
  edit: boolean
  data: PromoInterface
  setData: React.Dispatch<SetStateAction<PromoInterface>>
  getList: () => void
  setModalActive: React.Dispatch<SetStateAction<boolean>>
  el?: PromoInterface
}

export const Form = ({ edit, data, setData, getList, setModalActive, el }: Props) => {
  const {
    files,
    pairs,
    setFiles,
    setPairs,
    formAction,
    inputHandler,
    setDirty,
    getValidationResult,
    disabled,
    toggleAction,
    createCategorySelectList,
    multipleSearchSelectHandler,
    multipleSearchSelectRemoveHandler,
    createSaleItemSelectList,
    createStoreSelectList,
  } = useForm(data, setData, edit, getList, setModalActive, el)

  return (
    <div className={classes.container}>
      <div className={classes.title}>
        {data.id ? 'редактирование промо акции' : 'добавление промо акции'}
      </div>

      <Input
        type={'text'}
        fieldName={'name'}
        value={data.name}
        placeHolder={'Название промо акции'}
        onChangeHandler={inputHandler}
        itemValidationResult={getValidationResult('name')}
        onBlur={setDirty}
      />

      <Textarea
        type={'textarea'}
        fieldName={'description'}
        value={data.description}
        placeHolder="Опишите промо акцию подробно"
        rows={3}
        cols={20}
        onChangeHandler={inputHandler}
        itemValidationResult={getValidationResult('description')}
        onBlur={setDirty}
      />

      <Input
        label="Порядок сортировки"
        type={'text'}
        fieldName={'sortOrder'}
        value={data.sortOrder}
        placeHolder={'Порядок сортировки'}
        onChangeHandler={inputHandler}
        itemValidationResult={getValidationResult('sortOrder')}
        onBlur={setDirty}
      />

      {/* Toggle для первой покупки */}
      <div className={classes.toggleContainer}>
        <Toggle
          id="forFirstPurchase"
          onChange={() => toggleAction('forFirstPurchase')}
          checked={data.forFirstPurchase}
        />
        <div>Только на первую покупку</div>
      </div>

      {/* Toggle для товаров */}
      <div className={classes.toggleContainer}>
        <Toggle id="useItems" onChange={() => toggleAction('useItems')} checked={data.useItems} />
        <div>Скидка на конкретные товары</div>
      </div>

      {/* Поле выбора товаров (только если toggle включен) */}
      {data.useItems && (
        <SearchGroupSelect
          onBlur={setDirty}
          fieldName="itemsFor"
          options={createSaleItemSelectList()}
          value={data.itemsFor}
          onChangeHandler={multipleSearchSelectHandler}
          onRemoveHandler={multipleSearchSelectRemoveHandler}
          itemValidationResult={getValidationResult('itemsFor')}
          type="array"
          placeholder={
            createSaleItemSelectList().length
              ? 'Начните ввод и выберите товар из списка'
              : 'Сначала создайте товар'
          }
        />
      )}

      {/* Toggle для категорий */}
      <div className={classes.toggleContainer}>
        <Toggle
          id="useCategories"
          onChange={() => toggleAction('useCategories')}
          checked={data.useCategories}
        />
        <div>Скидка на категории товаров</div>
      </div>

      {/* Поле выбора категорий (только если toggle включен) */}
      {data.useCategories && (
        <SearchGroupSelect
          onBlur={setDirty}
          fieldName="categoriesFor"
          options={createCategorySelectList()}
          value={data.categoriesFor}
          onChangeHandler={multipleSearchSelectHandler}
          onRemoveHandler={multipleSearchSelectRemoveHandler}
          type="array"
          itemValidationResult={getValidationResult('categoriesFor')}
          placeholder={
            createCategorySelectList().length
              ? 'Начните ввод и выберите категорию из списка'
              : 'Сначала создайте категории'
          }
        />
      )}

      {/* Toggle для магазинов */}
      <div className={classes.toggleContainer}>
        <Toggle
          id="useStores"
          onChange={() => toggleAction('useStores')}
          checked={data.useStores}
        />
        <div>Ограничение по торговым точкам</div>
      </div>

      {/* Поле выбора магазинов (только если toggle включен) */}
      {data.useStores && (
        <SearchGroupSelect
          onBlur={setDirty}
          fieldName="storesFor"
          options={createStoreSelectList()}
          value={data.storesFor}
          onChangeHandler={multipleSearchSelectHandler}
          onRemoveHandler={multipleSearchSelectRemoveHandler}
          itemValidationResult={getValidationResult('storesFor')}
          type="array"
          placeholder={
            createStoreSelectList().length
              ? 'Начните ввод и выберите торговую точку из списка'
              : 'Сначала создайте торговые точки'
          }
        />
      )}

      {/* Поле скидки */}
      <Input
        type={'text'}
        fieldName={'discount'}
        value={data.discount ? data.discount : ''}
        placeHolder={'Скидка в процентах (%)'}
        onChangeHandler={inputHandler}
        itemValidationResult={getValidationResult('discount')}
        onBlur={setDirty}
      />

      {/* Toggle для промокода */}
      <div className={classes.toggleContainer}>
        <Toggle
          id="useDiscountCode"
          onChange={() => toggleAction('useDiscountCode')}
          checked={data.useDiscountCode}
        />
        <div>Требуется промокод</div>
      </div>

      {/* Поле промокода (только если toggle включен) */}
      {data.useDiscountCode && (
        <Input
          type={'text'}
          fieldName={'discountCode'}
          value={data.discountCode}
          placeHolder={'Введите промокод'}
          onChangeHandler={inputHandler}
          itemValidationResult={getValidationResult('discountCode')}
          onBlur={setDirty}
        />
      )}

      {/* Toggle для дат */}
      <div className={classes.toggleContainer}>
        <Toggle
          id="datesImportant"
          onChange={() => toggleAction('datesImportant')}
          checked={data.datesImportant}
        />
        <div>Указать сроки действия</div>
      </div>

      {/* Поля дат (только если toggle включен) */}
      {data.datesImportant && (
        <div className={classes.dates}>
          <div className={classes.date}>
            <Input
              label="Дата начала"
              type="datetime-local"
              fieldName="startDate"
              onChangeHandler={inputHandler}
              value={`${format(new Date(data.startDate), 'yyyy-MM-dd')}T${format(
                new Date(data.startDate),
                'HH:mm',
              )}`}
            />
          </div>
          <div className={classes.date}>
            <Input
              label="Дата завершения"
              type="datetime-local"
              fieldName="endDate"
              onChangeHandler={inputHandler}
              value={`${format(new Date(data.endDate), 'yyyy-MM-dd')}T${format(
                new Date(data.endDate),
                'HH:mm',
              )}`}
            />
          </div>
        </div>
      )}

      {/* НОВЫЙ Toggle: Скидка за самовывоз */}
      <div className={classes.toggleContainer}>
        <Toggle
          id="forPickup"
          onChange={() => toggleAction('forPickup')}
          checked={data.forPickup || false}
        />
        <div>Скидка за самовывоз</div>
      </div>

      {/* НОВЫЙ Toggle: Подарок за сумму */}
      <div className={classes.toggleContainer}>
        <Toggle
          id="giftBySum"
          onChange={() => toggleAction('giftBySum')}
          checked={data.giftBySum || false}
        />
        <div>Подарок за сумму</div>
      </div>

      {/* Общее поле minSum для giftBySum и forPickup */}
      {(data.giftBySum || data.forPickup) && (
        <Input
          type={'text'}
          fieldName={'minSum'}
          value={data.minSum || ''}
          placeHolder={
            data.giftBySum
              ? 'Минимальная сумма для подарка (₽)'
              : 'Минимальная сумма для скидки (₽)'
          }
          onChangeHandler={inputHandler}
          itemValidationResult={getValidationResult('minSum')}
          onBlur={setDirty}
        />
      )}

      {/* Поле выбора товаров-подарков (только для giftBySum) */}
      {data.giftBySum && (
        <SearchGroupSelect
          onBlur={setDirty}
          fieldName="giftItemIds"
          options={createSaleItemSelectList()}
          value={data.giftItemIds || []}
          onChangeHandler={multipleSearchSelectHandler}
          onRemoveHandler={multipleSearchSelectRemoveHandler}
          itemValidationResult={getValidationResult('giftItemIds')}
          type="array"
          placeholder={
            createSaleItemSelectList().length
              ? 'Выберите товары для подарка'
              : 'Сначала создайте товары'
          }
        />
      )}

      {/* Toggle активности */}
      <div className={classes.toggleContainer}>
        <Toggle id="isActive" onChange={() => toggleAction('isActive')} checked={data.isActive} />
        <div>Активна</div>
      </div>
      <div className={classes.toggleContainer}>
        <Toggle
          id="isVisible"
          onChange={() => toggleAction('isVisible')}
          checked={data.isVisible}
        />
        <div>Показывать клиентам</div>
      </div>
      <div className={classes.toggleContainer}>
        <Toggle id="showText" onChange={() => toggleAction('showText')} checked={data.showText} />
        <div>Показывать текст на баннере</div>
      </div>

      {/* Загрузка файлов */}
      <FileUpload
        length={1}
        min_length={1}
        extensions={['jpeg', 'png', 'jpg', 'webp', 'mp4', 'webm', 'mov']}
        files={files}
        setFiles={setFiles}
        pairs={pairs}
        setPairs={setPairs}
        maxVideoCount={1}
        totalLength={2} // ← видео + фото = 2
      />

      <Button text={edit ? 'Сохранить' : 'Добавить'} onClick={formAction} disabled={disabled} />
    </div>
  )
}
